import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Clock, CreditCard, ArrowDown, Trash2, Loader2 } from "lucide-react";
import axios from "axios";
import { useToast } from "../hooks/use-toast";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrialExpiredModal({ user, onAction }) {
  const [loading, setLoading] = useState(null);
  const { toast } = useToast();
  const trialTier = user?.trial_tier || "pro";

  const tierPrices = { advanced: "4.99", pro: "9.99", elite: "19.99" };
  const tierNames = { advanced: "Advanced", pro: "Pro", elite: "Elite" };

  const handleSubscribe = async () => {
    setLoading("subscribe");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/payments/create-subscription-checkout?tier=${trialTier}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch {
      toast({ title: "Error", description: "Failed to start checkout. Please try again.", variant: "destructive" });
      setLoading(null);
    }
  };

  const handleRevertFree = async () => {
    setLoading("revert");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/auth/revert-to-free`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Reverted to Free", description: "You're now on the free tier with limited features." });
      onAction("reverted");
    } catch {
      toast({ title: "Error", description: "Failed to revert. Please try again.", variant: "destructive" });
      setLoading(null);
    }
  };

  const handleDeleteAccount = async () => {
    const reason = window.prompt(
      "Sorry to see you go. Mind sharing why? (optional — helps us improve)\n\nTip: Cancel here keeps your account.",
      "",
    );
    if (reason === null) return; // user cancelled the prompt
    if (!window.confirm("Are you sure? This will permanently delete your account and all data. This cannot be undone.")) return;
    setLoading("delete");
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/auth/account`, {
        headers: { Authorization: `Bearer ${token}` },
        // Flows into deleted_users_archive.deletion_reason on the backend.
        data: { reason: reason?.trim() || null },
      });
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      toast({ title: "Account deleted", description: "Your account has been permanently deleted." });
      window.location.href = "/landing";
    } catch {
      toast({ title: "Error", description: "Failed to delete account. Please try again.", variant: "destructive" });
      setLoading(null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="max-w-md [&>button]:hidden" data-testid="trial-expired-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Clock className="w-5 h-5 text-amber-500" />
            Your Free Trial Has Ended
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-gray-600">
            Your 14-day {tierNames[trialTier]} trial has expired. Choose how you'd like to continue:
          </p>

          {/* Option 1: Subscribe */}
          <button
            onClick={handleSubscribe}
            disabled={!!loading}
            className="w-full flex items-start gap-3 p-4 rounded-lg border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors text-left disabled:opacity-50"
            data-testid="trial-subscribe-btn"
          >
            <CreditCard className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-gray-900 text-sm">Subscribe to {tierNames[trialTier]}</p>
              <p className="text-xs text-gray-500 mt-0.5">Continue with full access for £{tierPrices[trialTier]}/month</p>
            </div>
            {loading === "subscribe" && <Loader2 className="w-4 h-4 animate-spin text-blue-600 mt-1" />}
          </button>

          {/* Option 2: Revert to Free */}
          <button
            onClick={handleRevertFree}
            disabled={!!loading}
            className="w-full flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left disabled:opacity-50"
            data-testid="trial-revert-btn"
          >
            <ArrowDown className="w-5 h-5 text-gray-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-gray-900 text-sm">Revert to Free Tier</p>
              <p className="text-xs text-gray-500 mt-0.5">Keep your account with limited features (2 customers, 2 quotes, 2 invoices)</p>
            </div>
            {loading === "revert" && <Loader2 className="w-4 h-4 animate-spin text-gray-500 mt-1" />}
          </button>

          {/* Option 3: Delete Account */}
          <button
            onClick={handleDeleteAccount}
            disabled={!!loading}
            className="w-full flex items-start gap-3 p-3 rounded-lg border border-red-100 hover:bg-red-50 transition-colors text-left disabled:opacity-50"
            data-testid="trial-delete-btn"
          >
            <Trash2 className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-red-600 text-sm">Delete my account</p>
              <p className="text-xs text-gray-400 mt-0.5">Permanently remove all data</p>
            </div>
            {loading === "delete" && <Loader2 className="w-4 h-4 animate-spin text-red-400 mt-1" />}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
